import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

export const MeContext = createContext({ me: null, isAdmin: false });
export const useMe = () => useContext(MeContext);

export function useApi() {
  const { getToken } = useAuth();
  return useMemo(() => {
    const req = async (method, path, data, opts = {}) => {
      const token = await getToken();
      return axios({ method, url: `${API}${path}`, data, headers: { Authorization: `Bearer ${token}` }, ...opts });
    };
    return {
      get: (p, o) => req("get", p, undefined, o).then((r) => r.data),
      post: (p, d) => req("post", p, d || {}).then((r) => r.data),
      put: (p, d) => req("put", p, d).then((r) => r.data),
      patch: (p, d) => req("patch", p, d).then((r) => r.data),
      del: (p) => req("delete", p).then((r) => r.data),
      raw: req,
    };
  }, [getToken]);
}

export const errMsg = (e) => e?.response?.data?.detail || e?.message || "Request failed";

export function useFetch(path, deps = []) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try { setData(await api.get(path)); } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, api, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load, setData };
}

export function exportRows(rows, name, format) {
  const flat = rows.map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, Array.isArray(v) ? v.map((x) => (typeof x === "object" ? JSON.stringify(x) : x)).join("; ") : typeof v === "object" && v !== null ? JSON.stringify(v) : v])));
  const ws = XLSX.utils.json_to_sheet(flat);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 30));
  XLSX.writeFile(wb, `${name}-${new Date().toISOString().slice(0, 10)}.${format}`, { bookType: format });
}

export const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
export const fmtDay = (s) => (s ? new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—");
